import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  FileText,
  Plus,
  Trash2,
  Send,
  Eye,
  Settings2,
  ChevronDown,
  ChevronUp,
  Braces,
  Image,
  Layers,
  Sparkles,
  Link as LinkIcon,
  HelpCircle,
} from "lucide-react";
import { DiscordChannel, GuildConfig, api } from "@/lib/api";
import { Section, Field } from "@/components/panel/Section";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { ChannelPicker } from "@/components/panel/ChannelPicker";
import { toast } from "sonner";

interface Props {
  guildId: string;
  config: GuildConfig;
  channels: DiscordChannel[];
  onChange: (v: GuildConfig["embed_builder"]) => void;
}

interface CustomEmbed {
  id: string;
  name: string;
  channel_id?: string;
  webhook_url?: string;
  message_id?: string;
  content?: string;
  style?: "sections" | "embed";
  embed?: {
    title?: string;
    description?: string;
    url?: string;
    color?: string;
    author_name?: string;
    author_icon_url?: string;
    author_url?: string;
    image_url?: string;
    thumbnail_url?: string;
    footer_text?: string;
    footer_icon_url?: string;
    fields?: Array<{ name: string; value: string; inline: boolean }>;
  };
}

export function EmbedBuilderTab({ guildId, config, channels, onChange }: Props) {
  const cfg = config.embed_builder || { enabled: false, embeds: [] };
  const embeds = cfg.embeds || [];

  // Local state
  const [selectedId, setSelectedId] = useState<string>("");
  const [activeEmbed, setActiveEmbed] = useState<CustomEmbed | null>(null);

  // Accordion panels
  const [openPanels, setOpenPanels] = useState<Record<string, boolean>>({
    destination: true,
    author: false,
    body: true,
    media: false,
    fields: false,
    footer: false,
  });

  const togglePanel = (panel: string) => {
    setOpenPanels((prev) => ({ ...prev, [panel]: !prev[panel] }));
  };

  // Sync loaded configuration
  useEffect(() => {
    if (embeds.length > 0) {
      if (!selectedId) {
        setSelectedId(embeds[0].id);
        setActiveEmbed(embeds[0]);
      } else {
        const found = embeds.find((e) => e.id === selectedId);
        if (found) {
          setActiveEmbed(found);
        } else {
          setSelectedId(embeds[0].id);
          setActiveEmbed(embeds[0]);
        }
      }
    } else {
      setSelectedId("");
      setActiveEmbed(null);
    }
  }, [embeds, selectedId]);

  const updateConfig = (newEmbeds: CustomEmbed[], enabledState = cfg.enabled) => {
    onChange({
      enabled: enabledState,
      embeds: newEmbeds,
    });
  };

  const createNewEmbed = () => {
    const newId = `embed_${Date.now()}`;
    const newEmbed: CustomEmbed = {
      id: newId,
      name: `Custom Embed #${embeds.length + 1}`,
      channel_id: channels[0]?.id || "",
      webhook_url: "",
      message_id: "",
      content: "Hello from Areon Embed Builder! 👋",
      style: "sections", // Default to Section V2
      embed: {
        title: "Areon Custom V2 Announcement",
        description: "Configure this description, author header, accent colors, side thumbnails, large bottom attachments, and grid fields in real-time on the left!",
        color: "#5865F2",
        author_name: "Areon Bot",
        author_icon_url: "https://cdn.discordapp.com/embed/avatars/0.png",
        fields: [
          { name: "Support Panel", value: "Fully operational", inline: true },
          { name: "Live Preview", value: "Real-time updates", inline: true },
        ],
        footer_text: "Powered by Areon",
      },
    };

    const next = [...embeds, newEmbed];
    setSelectedId(newId);
    setActiveEmbed(newEmbed);
    updateConfig(next);
    toast.success("Created new custom embed template.");
  };

  const deleteActiveEmbed = () => {
    if (!activeEmbed) return;
    const next = embeds.filter((e) => e.id !== activeEmbed.id);
    setSelectedId(next[0]?.id || "");
    setActiveEmbed(next[0] || null);
    updateConfig(next);
    toast.success("Deleted embed template.");
  };

  const updateActiveEmbed = (updater: (prev: CustomEmbed) => CustomEmbed) => {
    if (!activeEmbed) return;
    const updated = updater(activeEmbed);
    setActiveEmbed(updated);

    const next = embeds.map((e) => (e.id === activeEmbed.id ? updated : e));
    updateConfig(next);
  };

  // Dispatch API hook
  const sendMutation = useMutation({
    mutationFn: (payload: any) => api.sendCustomEmbed(guildId, payload),
    onSuccess: (res) => {
      if (res.message_id) {
        updateActiveEmbed((prev) => ({ ...prev, message_id: res.message_id }));
        toast.success(`Embed sent successfully via ${res.via}! Message ID updated.`);
      } else {
        toast.success(`Embed sent successfully via ${res.via}!`);
      }
    },
    onError: (e: any) => {
      toast.error(e.message || "Failed to send embed message to Discord.", {
        duration: 10000,
        style: { whiteSpace: "pre-line" },
      });
    },
  });

  const handleSend = () => {
    if (!activeEmbed) return;

    const payload: any = {
      content: activeEmbed.content || "",
      style: activeEmbed.style ?? "sections",
      embed: activeEmbed.embed || {},
    };

    if (activeEmbed.webhook_url && activeEmbed.webhook_url.trim()) {
      payload.webhook_url = activeEmbed.webhook_url.trim();
    } else {
      payload.channel_id = activeEmbed.channel_id;
    }

    if (activeEmbed.message_id && activeEmbed.message_id.trim()) {
      payload.message_id = activeEmbed.message_id.trim();
    }

    sendMutation.mutate(payload);
  };

  // Nested property updaters
  const updateEmbedProp = (k: string, v: any) => {
    updateActiveEmbed((prev) => ({
      ...prev,
      embed: {
        ...(prev.embed || {}),
        [k]: v,
      },
    }));
  };

  const addField = () => {
    const fields = activeEmbed?.embed?.fields || [];
    if (fields.length >= 25) {
      toast.error("Discord embeds allow at most 25 fields.");
      return;
    }
    const next = [...fields, { name: "New Field", value: "Field Value", inline: false }];
    updateEmbedProp("fields", next);
  };

  const removeField = (idx: number) => {
    const fields = activeEmbed?.embed?.fields || [];
    const next = fields.filter((_, i) => i !== idx);
    updateEmbedProp("fields", next);
  };

  const updateField = (idx: number, k: "name" | "value" | "inline", v: any) => {
    const fields = activeEmbed?.embed?.fields || [];
    const next = fields.map((f, i) => (i === idx ? { ...f, [k]: v } : f));
    updateEmbedProp("fields", next);
  };

  return (
    <Section
      icon={FileText}
      title="Custom Embed Builder (Discohook Clone)"
      description="Design rich announcements, welcome embeds, and complex rules lists. Send or update them via Areon or custom Discord webhooks in real-time."
      enabled={cfg.enabled}
      onToggle={(v) => updateConfig(embeds, v)}
    >
      {/* Configuration Header Controls */}
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between pb-4 border-b border-border/60">
        <div className="flex flex-wrap items-center gap-2">
          {embeds.length > 0 ? (
            <select
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              className="h-9 rounded-md border border-border bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-primary min-w-[200px]"
            >
              {embeds.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </select>
          ) : (
            <span className="text-sm text-muted-foreground italic">No templates created. Click new →</span>
          )}
          <Button size="sm" variant="outline" onClick={createNewEmbed} className="h-9 gap-1.5 border-dashed">
            <Plus className="h-4 w-4" /> New Embed
          </Button>
        </div>

        {activeEmbed && (
          <div className="flex gap-2">
            <Input
              size={30}
              className="h-9 text-xs"
              value={activeEmbed.name}
              onChange={(e) => updateActiveEmbed((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Template name"
            />
            <Button size="sm" variant="destructive" onClick={deleteActiveEmbed} className="h-9" title="Delete Template">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {!activeEmbed ? (
        <Card className="p-12 text-center border-dashed bg-muted/10 space-y-4">
          <Layers className="h-12 w-12 text-muted-foreground/60 mx-auto animate-pulse" />
          <div className="space-y-1">
            <h4 className="font-bold text-base">Select or Create an Embed Template</h4>
            <p className="text-sm text-muted-foreground max-w-sm mx-auto">
              Create a template to access the live composer, color selector, custom fields grid, and real-time previews.
            </p>
          </div>
          <Button onClick={createNewEmbed} className="bg-primary/95 hover:bg-primary gap-1">
            <Plus className="h-4 w-4" /> Get Started
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_minmax(320px,460px)] gap-6 items-start">
          {/* LEFT: Composer Controls */}
          <div className="space-y-4">
            {/* Panel 1: Destination Selection */}
            <Card className="border border-border/80 overflow-hidden">
              <button
                type="button"
                onClick={() => togglePanel("destination")}
                className="w-full p-4 flex items-center justify-between font-bold text-sm bg-muted/20 hover:bg-muted/40 transition-colors"
              >
                <span className="flex items-center gap-2">
                  <Settings2 className="h-4 w-4 text-primary" /> 1. Delivery & Target Destination
                </span>
                {openPanels.destination ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              {openPanels.destination && (
                <div className="p-4 border-t border-border/60 space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <Field
                      label="Delivery Method"
                      hint="Choose whether the bot posts natively, or use an external webhook."
                    >
                      <div className="flex gap-2 p-1 bg-muted/40 rounded-lg border border-border">
                        <button
                          type="button"
                          className={`flex-1 text-xs py-1.5 rounded font-bold transition-all ${
                            !activeEmbed.webhook_url
                              ? "bg-background text-primary shadow-sm"
                              : "text-muted-foreground hover:text-foreground"
                          }`}
                          onClick={() => updateActiveEmbed((prev) => ({ ...prev, webhook_url: "" }))}
                        >
                          Discord Channel
                        </button>
                        <button
                          type="button"
                          className={`flex-1 text-xs py-1.5 rounded font-bold transition-all ${
                            activeEmbed.webhook_url
                              ? "bg-background text-primary shadow-sm"
                              : "text-muted-foreground hover:text-foreground"
                          }`}
                          onClick={() => updateActiveEmbed((prev) => ({ ...prev, webhook_url: "https://discord.com/api/webhooks/" }))}
                        >
                          Custom Webhook
                        </button>
                      </div>
                    </Field>

                    {activeEmbed.webhook_url !== undefined && activeEmbed.webhook_url !== "" ? (
                      <Field label="Webhook URL" hint="Enter discord.com/api/webhooks/... URL.">
                        <Input
                          value={activeEmbed.webhook_url}
                          onChange={(e) => updateActiveEmbed((prev) => ({ ...prev, webhook_url: e.target.value }))}
                          placeholder="https://discord.com/api/webhooks/..."
                          className="font-mono text-xs"
                        />
                      </Field>
                    ) : (
                      <Field label="Target Channel" hint="Where the bot should post this embed.">
                        <ChannelPicker
                          channels={channels}
                          value={activeEmbed.channel_id || ""}
                          onChange={(v) => updateActiveEmbed((prev) => ({ ...prev, channel_id: v }))}
                        />
                      </Field>
                    )}
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 pt-2 border-t border-border/40">
                    <Field
                      label="Message Format Style"
                      hint="Choose between Discord's new Section V2 container format (default) or classic rich Embed styling."
                    >
                      <div className="flex gap-2 p-1 bg-muted/40 rounded-lg border border-border">
                        <button
                          type="button"
                          className={`flex-1 text-xs py-1.5 rounded font-bold transition-all ${
                            (activeEmbed.style ?? "sections") === "sections"
                              ? "bg-background text-primary shadow-sm"
                              : "text-muted-foreground hover:text-foreground"
                          }`}
                          onClick={() => updateActiveEmbed((prev) => ({ ...prev, style: "sections" }))}
                        >
                          Section V2 (Default)
                        </button>
                        <button
                          type="button"
                          className={`flex-1 text-xs py-1.5 rounded font-bold transition-all ${
                            activeEmbed.style === "embed"
                              ? "bg-background text-primary shadow-sm"
                              : "text-muted-foreground hover:text-foreground"
                          }`}
                          onClick={() => updateActiveEmbed((prev) => ({ ...prev, style: "embed" }))}
                        >
                          Classic Embed
                        </button>
                      </div>
                    </Field>

                    <Field
                      label="Message ID (Optional)"
                      hint="Provide an active Message ID to edit/overwrite that message instead of posting a new one."
                    >
                      <Input
                        value={activeEmbed.message_id || ""}
                        onChange={(e) => updateActiveEmbed((prev) => ({ ...prev, message_id: e.target.value }))}
                        placeholder="e.g. 102456789012345678"
                        className="font-mono text-xs"
                      />
                    </Field>
                  </div>

                  <div className="pt-2 border-t border-border/40">
                    <Field
                      label="Message Plain Text Content (Optional)"
                      hint="Outer message text showing outside the embed card."
                    >
                      <Textarea
                        value={activeEmbed.content || ""}
                        onChange={(e) => updateActiveEmbed((prev) => ({ ...prev, content: e.target.value }))}
                        placeholder="e.g. Hey everyone, check out this announcement!"
                        rows={2}
                      />
                    </Field>
                  </div>
                </div>
              )}
            </Card>

            {/* Panel 2: Author Header */}
            <Card className="border border-border/80 overflow-hidden">
              <button
                type="button"
                onClick={() => togglePanel("author")}
                className="w-full p-4 flex items-center justify-between font-bold text-sm bg-muted/20 hover:bg-muted/40 transition-colors"
              >
                <span className="flex items-center gap-2">
                  <Eye className="h-4 w-4 text-sky-400" /> 2. Author Header Details
                </span>
                {openPanels.author ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              {openPanels.author && (
                <div className="p-4 border-t border-border/60 grid sm:grid-cols-1 md:grid-cols-3 gap-4">
                  <Field label="Author Name" hint="Shown at the very top.">
                    <Input
                      value={activeEmbed.embed?.author_name || ""}
                      onChange={(e) => updateEmbedProp("author_name", e.target.value)}
                      placeholder="e.g. Staff Team"
                    />
                  </Field>
                  <Field label="Author Icon URL" hint="Avatar icon image.">
                    <Input
                      value={activeEmbed.embed?.author_icon_url || ""}
                      onChange={(e) => updateEmbedProp("author_icon_url", e.target.value)}
                      placeholder="https://..."
                    />
                  </Field>
                  <Field label="Author Link URL" hint="Clicking author name navigates here.">
                    <Input
                      value={activeEmbed.embed?.author_url || ""}
                      onChange={(e) => updateEmbedProp("author_url", e.target.value)}
                      placeholder="https://..."
                    />
                  </Field>
                </div>
              )}
            </Card>

            {/* Panel 3: Body Content */}
            <Card className="border border-border/80 overflow-hidden">
              <button
                type="button"
                onClick={() => togglePanel("body")}
                className="w-full p-4 flex items-center justify-between font-bold text-sm bg-muted/20 hover:bg-muted/40 transition-colors"
              >
                <span className="flex items-center gap-2">
                  <Braces className="h-4 w-4 text-violet-400" /> 3. Body Title & Description
                </span>
                {openPanels.body ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              {openPanels.body && (
                <div className="p-4 border-t border-border/60 space-y-4">
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                      <Field label="Embed Title" hint="Primary bold heading.">
                        <Input
                          value={activeEmbed.embed?.title || ""}
                          onChange={(e) => updateEmbedProp("title", e.target.value)}
                          placeholder="e.g. Welcome Announcement"
                        />
                      </Field>
                    </div>
                    <Field label="Accent Color" hint="Hex color code for the side strip.">
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={activeEmbed.embed?.color || "#5865F2"}
                          onChange={(e) => updateEmbedProp("color", e.target.value)}
                          className="h-9 w-12 p-0.5 rounded cursor-pointer shrink-0 border border-border"
                        />
                        <Input
                          value={activeEmbed.embed?.color || "#5865F2"}
                          onChange={(e) => updateEmbedProp("color", e.target.value)}
                          placeholder="#5865F2"
                          className="font-mono text-xs uppercase"
                        />
                      </div>
                    </Field>
                  </div>

                  <div className="grid md:grid-cols-1 gap-4">
                    <Field label="Title Link URL (Optional)" hint="Clicking title header opens this link.">
                      <Input
                        value={activeEmbed.embed?.url || ""}
                        onChange={(e) => updateEmbedProp("url", e.target.value)}
                        placeholder="https://..."
                      />
                    </Field>
                    <Field label="Description (Supports Markdown)" hint="Primary body content text. Formats like bold, bullet lists, or mentions work.">
                      <Textarea
                        value={activeEmbed.embed?.description || ""}
                        onChange={(e) => updateEmbedProp("description", e.target.value)}
                        placeholder="Write your embed description body here..."
                        rows={5}
                        className="font-sans leading-relaxed"
                      />
                    </Field>
                  </div>
                </div>
              )}
            </Card>

            {/* Panel 4: Media Attachments */}
            <Card className="border border-border/80 overflow-hidden">
              <button
                type="button"
                onClick={() => togglePanel("media")}
                className="w-full p-4 flex items-center justify-between font-bold text-sm bg-muted/20 hover:bg-muted/40 transition-colors"
              >
                <span className="flex items-center gap-2">
                  <Image className="h-4 w-4 text-emerald-400" /> 4. Rich Media & Images
                </span>
                {openPanels.media ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              {openPanels.media && (
                <div className="p-4 border-t border-border/60 grid sm:grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Thumbnail URL (Top-Right)" hint="Small image floated at the upper-right corner.">
                    <Input
                      value={activeEmbed.embed?.thumbnail_url || ""}
                      onChange={(e) => updateEmbedProp("thumbnail_url", e.target.value)}
                      placeholder="https://..."
                    />
                  </Field>
                  <Field label="Large Image URL (Bottom)" hint="Large full-width image attached at the bottom.">
                    <Input
                      value={activeEmbed.embed?.image_url || ""}
                      onChange={(e) => updateEmbedProp("image_url", e.target.value)}
                      placeholder="https://..."
                    />
                  </Field>
                </div>
              )}
            </Card>

            {/* Panel 5: Custom Fields Grid */}
            <Card className="border border-border/80 overflow-hidden">
              <button
                type="button"
                onClick={() => togglePanel("fields")}
                className="w-full p-4 flex items-center justify-between font-bold text-sm bg-muted/20 hover:bg-muted/40 transition-colors"
              >
                <span className="flex items-center gap-2">
                  <Layers className="h-4 w-4 text-amber-400" /> 5. Custom Grid Fields ({activeEmbed.embed?.fields?.length || 0})
                </span>
                {openPanels.fields ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              {openPanels.fields && (
                <div className="p-4 border-t border-border/60 space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground">Add metadata fields to structure welcome states or rules grids.</span>
                    <Button size="sm" variant="outline" onClick={addField} className="h-8 gap-1">
                      <Plus className="h-3.5 w-3.5" /> Add Field
                    </Button>
                  </div>

                  {(!activeEmbed.embed?.fields || activeEmbed.embed.fields.length === 0) ? (
                    <div className="text-center py-6 text-xs text-muted-foreground italic border border-dashed rounded-lg">
                      No custom fields added. Click Add Field above.
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {activeEmbed.embed.fields.map((f, i) => (
                        <div key={i} className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3 p-3 border border-border/40 rounded-xl bg-muted/10 relative group/row">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 flex-1">
                            <Field label="Field Name" hint="" className="mb-0">
                              <Input
                                value={f.name}
                                onChange={(e) => updateField(i, "name", e.target.value)}
                                placeholder="Field Title"
                              />
                            </Field>
                            <Field label="Field Value" hint="" className="mb-0">
                              <Input
                                value={f.value}
                                onChange={(e) => updateField(i, "value", e.target.value)}
                                placeholder="Field Content"
                              />
                            </Field>
                          </div>
                          
                          <div className="flex items-center gap-3 shrink-0 py-1">
                            <label className="flex items-center gap-1.5 cursor-pointer text-xs font-semibold select-none text-muted-foreground hover:text-foreground">
                              <Switch
                                checked={f.inline}
                                onCheckedChange={(v) => updateField(i, "inline", v)}
                                className="scale-75 origin-left"
                              />
                              Inline
                            </label>
                            
                            <Button size="icon" variant="ghost" onClick={() => removeField(i)} className="h-8 w-8 text-destructive hover:bg-destructive/10">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Card>

            {/* Panel 6: Footer Details */}
            <Card className="border border-border/80 overflow-hidden">
              <button
                type="button"
                onClick={() => togglePanel("footer")}
                className="w-full p-4 flex items-center justify-between font-bold text-sm bg-muted/20 hover:bg-muted/40 transition-colors"
              >
                <span className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-pink-400" /> 6. Footer Information
                </span>
                {openPanels.footer ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              {openPanels.footer && (
                <div className="p-4 border-t border-border/60 grid sm:grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Footer Text" hint="Small text at the absolute bottom.">
                    <Input
                      value={activeEmbed.embed?.footer_text || ""}
                      onChange={(e) => updateEmbedProp("footer_text", e.target.value)}
                      placeholder="e.g. Powered by Orbit Announcement Suite"
                    />
                  </Field>
                  <Field label="Footer Icon URL" hint="Tiny image next to footer text.">
                    <Input
                      value={activeEmbed.embed?.footer_icon_url || ""}
                      onChange={(e) => updateEmbedProp("footer_icon_url", e.target.value)}
                      placeholder="https://..."
                    />
                  </Field>
                </div>
              )}
            </Card>

            {/* Dispatch Action Controls */}
            <Card className="p-4 bg-muted/25 border border-primary/20 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="space-y-0.5 text-center sm:text-left">
                <div className="font-bold text-sm">Deploy Setup to Discord</div>
                <div className="text-xs text-muted-foreground">
                  {activeEmbed.webhook_url
                    ? "Deliver embed instantly as a webhook message."
                    : "Post natively to your selected Discord channel."}
                </div>
              </div>

              <Button
                disabled={sendMutation.isPending}
                onClick={handleSend}
                className="w-full sm:w-auto bg-primary hover:bg-primary/90 shadow-md shadow-primary/20 gap-1.5 font-bold shrink-0"
              >
                <Send className="h-4 w-4" />
                {activeEmbed.message_id ? "Edit/Update Sent Message" : "Post Embed to Server"}
              </Button>
            </Card>
          </div>

          {/* RIGHT: Live Discord Mockup Preview */}
          <div className="lg:sticky lg:top-6 space-y-3">
            <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <Eye className="h-4 w-4" /> Discord Live Preview
            </div>

            {/* Simulated Discord Window */}
            <div className="rounded-xl border border-border/70 bg-[#313338] text-[#dbdee1] p-4 font-sans text-sm shadow-2xl overflow-hidden leading-relaxed text-left">
              {/* Message Header Mock */}
              <div className="flex gap-4 items-start select-none">
                <div className="h-10 w-10 rounded-full bg-[#5865F2] flex items-center justify-center shrink-0 shadow-inner">
                  <img
                    src="/areon-logo.png"
                    alt=""
                    className="h-6 w-6 object-contain"
                    onError={(e) => {
                      (e.target as HTMLElement).style.display = "none";
                    }}
                  />
                </div>
                <div className="space-y-1.5 flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-[#f2f3f5] text-[15px] hover:underline cursor-pointer">
                      {activeEmbed.webhook_url ? "Areon Webhook" : "Areon Bot"}
                    </span>
                    <span className="bg-[#5865f2] text-white text-[10px] px-1 py-0.5 rounded font-semibold tracking-wide flex items-center justify-center h-4.5 uppercase">
                      APP
                    </span>
                    <span className="text-[11px] text-[#949ba4] font-medium mt-0.5">Today at 3:30 PM</span>
                  </div>

                  {/* Plain text content (if any) */}
                  {activeEmbed.content && (
                    <div className="text-[#dbdee1] text-[15px] whitespace-pre-wrap break-words leading-relaxed select-text select-all selection:bg-primary/25">
                      {activeEmbed.content}
                    </div>
                  )}

                  {/* Section V2 or Rich Embed container */}
                  {activeEmbed.embed && (
                    (activeEmbed.style ?? "sections") === "sections" ? (
                      /* Section V2 Mock Container */
                      <div className="rounded-xl bg-[#2b2d31] max-w-[520px] border border-[#1e1f22] p-4 space-y-3 w-full shadow-lg">
                        {/* Author */}
                        {activeEmbed.embed.author_name && (
                          <div className="text-[15px] font-bold text-white leading-snug">
                            {activeEmbed.embed.author_name}
                          </div>
                        )}

                        {/* Title */}
                        {activeEmbed.embed.title && (
                          <div className="text-[19px] font-extrabold text-white tracking-tight leading-tight">
                            {activeEmbed.embed.title}
                          </div>
                        )}

                        {/* Description */}
                        {activeEmbed.embed.description && (
                          <div className="text-[#dbdee1] text-[14px] leading-relaxed whitespace-pre-wrap break-words select-text">
                            {activeEmbed.embed.description}
                          </div>
                        )}

                        {/* Media Image */}
                        {(activeEmbed.embed.image_url || activeEmbed.embed.thumbnail_url) && (
                          <div className="rounded overflow-hidden select-none border border-[#1e1f22] pt-1">
                            <img
                              src={activeEmbed.embed.image_url || activeEmbed.embed.thumbnail_url}
                              className="max-h-[260px] w-full object-cover rounded"
                              alt=""
                              onError={(e) => {
                                (e.target as HTMLElement).style.display = "none";
                              }}
                            />
                          </div>
                        )}

                        {/* Separator & Custom fields list */}
                        {activeEmbed.embed.fields && activeEmbed.embed.fields.length > 0 && (
                          <div className="space-y-3 pt-2">
                            <div className="h-[1px] bg-[#3f4147] w-full" />
                            {activeEmbed.embed.fields.map((f, i) => (
                              <div key={i} className="text-sm leading-normal p-2.5 rounded bg-[#1e1f22]/40 border border-[#3f4147]/30 space-y-1">
                                <div className="text-[11px] font-bold text-[#b5bac1] uppercase tracking-wider">
                                  {f.name || "\u200b"}
                                </div>
                                <div className="text-[13px] text-white font-medium whitespace-pre-wrap">
                                  {f.value || "\u200b"}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Footer */}
                        {activeEmbed.embed.footer_text && (
                          <div className="space-y-2 pt-2 select-none">
                            <div className="h-[1px] bg-[#3f4147] w-full" />
                            <div className="text-[11px] text-[#949ba4] font-medium leading-none">
                              {activeEmbed.embed.footer_text}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      /* Classic Rich Embed Container */
                      <div
                        className="rounded bg-[#2b2d31] max-w-[520px] flex overflow-hidden border-l-4 w-full shadow-lg"
                        style={{ borderLeftColor: activeEmbed.embed.color || "#5865F2" }}
                      >
                        <div className="p-3 flex-1 space-y-2">
                          {/* Embed Author Header */}
                          {activeEmbed.embed.author_name && (
                            <div className="flex items-center gap-2 select-none">
                              {activeEmbed.embed.author_icon_url && (
                                <img
                                  src={activeEmbed.embed.author_icon_url}
                                  className="h-6 w-6 rounded-full object-cover shrink-0"
                                  alt=""
                                  onError={(e) => {
                                    (e.target as HTMLElement).style.display = "none";
                                  }}
                                />
                              )}
                              <span
                                className={`text-xs font-bold text-white leading-tight ${
                                  activeEmbed.embed.author_url ? "hover:underline cursor-pointer" : ""
                                }`}
                              >
                                {activeEmbed.embed.author_name}
                              </span>
                            </div>
                          )}

                          {/* Embed Title & Side Thumbnail Header grid layout */}
                          <div className="flex items-start justify-between gap-4">
                            <div className="space-y-1.5 flex-1 min-w-0">
                              {activeEmbed.embed.title && (
                                <h3
                                  className={`text-[16px] font-bold text-white tracking-tight leading-snug break-words ${
                                    activeEmbed.embed.url ? "text-[#00a8fc] hover:underline cursor-pointer" : ""
                                  }`}
                                >
                                  {activeEmbed.embed.title}
                                </h3>
                              )}

                              {/* Embed Description body content */}
                              {activeEmbed.embed.description && (
                                <div className="text-[#dbdee1] text-[14px] leading-relaxed whitespace-pre-wrap break-words select-text">
                                  {activeEmbed.embed.description}
                                </div>
                              )}
                            </div>

                            {/* Float Thumbnail right */}
                            {activeEmbed.embed.thumbnail_url && (
                              <img
                                src={activeEmbed.embed.thumbnail_url}
                                className="h-20 w-20 rounded object-cover shrink-0 border border-[#1e1f22]"
                                alt=""
                                onError={(e) => {
                                  (e.target as HTMLElement).style.display = "none";
                                }}
                              />
                            )}
                          </div>

                          {/* Custom fields list */}
                          {activeEmbed.embed.fields && activeEmbed.embed.fields.length > 0 && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                              {activeEmbed.embed.fields.map((f, i) => (
                                <div
                                  key={i}
                                  className={`space-y-0.5 leading-snug break-words ${
                                    f.inline ? "" : "sm:col-span-2"
                                  }`}
                                >
                                  <div className="text-[13px] font-bold text-white tracking-tight">
                                    {f.name || "\u200b"}
                                  </div>
                                  <div className="text-[13px] text-[#dbdee1] font-medium leading-normal whitespace-pre-wrap">
                                    {f.value || "\u200b"}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Large bottom image */}
                          {activeEmbed.embed.image_url && (
                            <div className="pt-2 select-none">
                              <img
                                src={activeEmbed.embed.image_url}
                                className="max-h-[300px] w-full rounded object-cover border border-[#1e1f22]"
                                alt=""
                                onError={(e) => {
                                  (e.target as HTMLElement).style.display = "none";
                                }}
                              />
                            </div>
                          )}

                          {/* Embed Footer Info */}
                          {activeEmbed.embed.footer_text && (
                            <div className="flex items-center gap-2 text-[12px] text-[#949ba4] font-medium pt-1 select-none border-t border-border/10">
                              {activeEmbed.embed.footer_icon_url && (
                                <img
                                  src={activeEmbed.embed.footer_icon_url}
                                  className="h-5 w-5 rounded-full object-cover shrink-0"
                                  alt=""
                                  onError={(e) => {
                                    (e.target as HTMLElement).style.display = "none";
                                  }}
                                />
                              )}
                              <span className="leading-tight break-words">
                                {activeEmbed.embed.footer_text}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Section>
  );
}
